export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

/**
 * Canvas font stack — modern browsers do per-glyph fallback through this list, so Thai/JP/KR
 * glyphs render with their proper Noto faces (with tone-mark shaping) while Latin/UI text
 * still uses system-ui. Apply on every Phaser Text object that may render pack content.
 *
 * "Twemoji Country Flags" leads the list so the celebration screen's flag renders on Windows/
 * Chromium (whose native emoji font lacks flags). It's only defined on those platforms and only
 * for flag code points (see src/platform/countryFlagFont.ts), so it never affects other glyphs.
 */
export const FONT_STACK = '"Twemoji Country Flags", "Sarabun", "Noto Sans JP", "Noto Sans KR", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const STARTING_LIVES = 5;

export const BOARD = {
  durationMs: 30_000,
  wordsPerBoard: 6,
  spawnIntervalMs: 1200,
};

export const SCORING = {
  correctHit: 10,
  wrongHit: 0,
  missedRequiredWord: 0,
};

/**
 * Bodies in the in-game solar system (src/game/ui/PlanetStack.ts) — all eight planets, Mercury
 * through Neptune, and then the Sun.
 * Lighting every one of them in a single game is the run's ceiling and earns the rocket-and-
 * confetti send-off — the answer to a strong player having nothing left to reach for.
 */
export const STAR_STACK_SLOTS = 9;

/**
 * Accuracy reward: boards cleared in a row without losing a heart. Score cannot express accuracy —
 * a wrong tap costs 0 points, only a heart — so a clean streak is the only honest measure of care,
 * and this is what it buys: the ABC-star "Perfekt!" overlay between boards.
 *
 * Deliberately a repeating milestone rather than a one-off: it fires on every multiple, so a child
 * on a long clean run is told at 10, 20, 30 … and the message carries the real number.
 */
export const PERFECT_STREAK_STEP = 10;

/** The streak after a board is cleared: a lost heart anywhere in it resets the run to zero. */
export function nextCleanStreak(streak: number, boardHadError: boolean): number {
  return boardHadError ? 0 : streak + 1;
}

/**
 * The clean-board streak worth celebrating right now, or null. `step` is a parameter only so the
 * dev flag `?perfect[=n]` can shorten it — otherwise verifying the overlay means clearing ten
 * flawless boards by hand. Kept pure (no `location` access) so it stays testable in node.
 */
export function perfectMilestone(streak: number, step: number = PERFECT_STREAK_STEP): number | null {
  return streak > 0 && step > 0 && streak % step === 0 ? streak : null;
}

/**
 * Crumbling Board — slow-play penalty for the timed lives mode (specs/crumbling-board.md, retuned
 * by specs/adaptive-difficulty.md).
 * Run unusually slow *for you* — more than `spreadK` of your own standard deviations past your own
 * recent pace — and the board starts falling apart: word-planks pop off their nails one after
 * another and tumble off the bottom. A dropped *target* word costs a life. Both the pace and the
 * spread are session-local (see RoundScene), so they reset on menu/restart.
 *  - spreadK: how many of the player's own deviations past their own pace the board may run
 *  - cascadeMinMs / cascadeMaxMs: gap between successive words starting to fall — randomized in
 *    this range so the collapse reads as "one after another" rather than a mechanical metronome
 *  - cornerGapMs: beat between the first corner giving way (the word tilts, pivoting on the other
 *    corner) and the second corner letting go (it falls). No box is drawn — the plank was only ever
 *    the motion analogy; the word itself does the tilt-then-fall.
 *  - fallMs: duration of the slow ("medium speed") fall off the bottom of the screen
 */
export const CRUMBLE = {
  /**
   * How many of the player's OWN typical deviations a board may run over their own typical time
   * before it starts to crumble. See specs/adaptive-difficulty.md §1.
   *
   * This replaced a fixed `thresholdMult: 2`. A fixed multiple of the median looks like one rule but
   * is a different rule for every player, because the board crumbles on the TAIL of their board-time
   * distribution while the threshold was set from its MIDDLE. Measured, `2x median` sat 2.0
   * standard deviations above a first-grader's own pace and 9.8 above an adult's — so it fired on
   * 7.6% of a first-grader's boards and 0.3% of an adult's, which is the opposite of the intent.
   * Consistency is exactly what develops with age, so keying on a fixed multiple of the median is
   * structurally a rule that punishes being young.
   *
   * `k` asks the same question of everyone in units they each supply themselves, so the mechanic
   * needs no idea who is playing — and the spread doubles as the age proxy we deliberately never
   * store.
   *
   * Calibrated empirically, NOT from the normal table: k=2.4 gives ~7% of boards, where P(Z>2.4)
   * would say 0.8%. The gap is the estimator, and it is deliberate — `pace` is the median of only
   * three boards so it can follow a child who is tiring, and that responsiveness costs precision.
   * The jitter fattens the effective tail, so k has to be read off the measured sweep
   * (tools/difficulty/RESULTS.md) rather than derived.
   */
  spreadK: 2.4,
  /**
   * Floor under the standard threshold — a second net under `minSpreadRatio`, and the reason a
   * degenerate pace/spread estimate can never produce an unplayable clock.
   */
  defaultMinMs: 2000,
  /**
   * Picture Words is aimed at kindergarten / grade 1, where the child is decoding eight words
   * letter by letter rather than skimming a category — so it gets a more forgiving `k` and a higher
   * absolute floor. See specs/picture-word-mode.md §2.6.
   */
  pictureSpreadK: 3.0,
  pictureMinMs: 4500,
  /**
   * Banner-context lessons ("Was fehlt?", Thai classifiers, JA/KO particles) put the *question* in
   * the banner instead of a fixed rule, so the child has to read the banner before a single tile
   * makes sense — and for the gap lesson that banner is a whole sentence. The clock times only the
   * tile-scanning half and would otherwise start crumbling mid-sentence, so these boards get the
   * same treatment as Picture Words with a higher floor: a sentence takes longer to read than a
   * picture takes to recognise. See specs/gap-mode.md.
   */
  bannerSpreadK: 3.0,
  bannerMinMs: 6000,
  /**
   * Floor on the measured spread (in log units, i.e. a relative spread). A handful of boards that
   * happened to land almost on top of each other would otherwise collapse the threshold onto the
   * pace itself. Deliberately well BELOW any real player's spread — a metronomic adult measures
   * ~0.09 — so this is a safety net, never a policy lever: at 0.20 it bound on every adult board and
   * quietly undid the rule it was meant to protect.
   */
  minSpreadRatio: 0.04,
  /**
   * Below this measured spread a player is treated as *consistent*, and the allowance `k` is
   * pressed towards its `consistencyPress` fraction — see specs/adaptive-difficulty.md §6.
   *
   * `spreadK` alone equalises: it gives every player the same statistical headroom in their own
   * deviations, which is what §1 set out to do and is the right floor for the mechanic. But
   * equalising is not the same as pressing, and a metronomic player has so little spread that
   * `exp(k × spread)` is barely above 1 however `k` is set — measured, the crumble fires on 12% of an
   * adult's boards and costs them 0.001 hearts a board. The knee is where the design stops being
   * purely fair and starts asking more of a player who has demonstrably got the measure of the game.
   *
   * The knee is keyed on the one thing that separates the cohorts and that a child cannot cross by
   * playing longer or better — unlike stars, which are throughput, and which a strong first-grader
   * on a long clean run would eventually reach.
   *
   * 0.10 is MEASURED, and it is far below where the cohort table would have put it. The gap looks
   * wide — an adult measures 0.07 against a first-grader's 0.31–0.77 — but the knee is compared
   * against an ESTIMATE from eight boards, and a first-grader's estimate dips well under their true
   * spread often enough to matter. Both wider candidates leaked into grade 1 and the sweep saw it:
   *
   *   knee 0.20 → +0.7 to +1.5 pp of grade-1 crumble rate (three lessons, 8 000 boards each)
   *   knee 0.14 → +0.6 pp mean, positive in ALL SIX grade-1 cells — small, but not noise
   *   knee 0.10 → −0.05 pp mean, signs mixed: grade 1 is untouched, which is the requirement
   *
   * and the adult barely notices the difference (18.8% at 0.14, 19.0% at 0.10), because they sit at
   * 0.07 and are well inside every candidate. So the tightest knee that still covers the adult wins.
   *
   * Re-measure if `SPREAD_WINDOW` changes: the safe knee is a property of how noisy an eight-board
   * estimate is, not of where the cohorts actually sit. And note what it does NOT buy — grade 2
   * (+1.1 pp) and grade 3 (+2.8 pp) do move, through the same estimator dips and through the lower
   * rescue floor. Both stay inside their bands and neither loses more hearts, but "adults only" is
   * true of the rule, not of the measured outcome.
   */
  spreadKnee: 0.10,
  /**
   * How much of `k` the press may take from a perfectly metronomic player (spread → 0). The
   * reduction ramps linearly from 0 at the knee to this fraction at zero spread, so it is
   * continuous at the knee and the threshold still rises monotonically with spread — a more
   * scattered player can never end up with a tighter clock than a steadier one.
   *
   * **Currently 0 — the press is retired, not deleted.** See specs/adaptive-difficulty.md §7: the
   * pace-graded `clockTrim` below aims at the same player and reaches them harder, so running both
   * stacks two presses on one cohort — measured, the modelled adult goes to 45% of boards at a press
   * of 0.3 and 53% at 0.5, against 35% with the trim alone and an 18–35% target band.
   *
   * The rule and its calibration are kept because the two levers key on DIFFERENT axes and the pair
   * may yet be wanted: the trim reads absolute speed, the press reads consistency. A fast but
   * erratic player meets only the trim; a slow but metronomic one only the press. If the adult game
   * ever needs more than the trim gives it, 0.3 is the next value to measure, not 0.85.
   *
   * Its own calibration, for that day: at `spreadKnee` 0.10, 0.85 carried the modelled adult from
   * 12.2% of boards to 19.0% while every grade-1 cell stayed put. A press and a knee trade off
   * against each other, so the two are only meaningful as a pair.
   */
  consistencyPress: 0,
  // Every collapse timing below is slower than it first shipped (1200/1800/500/1400): 30% on all
  // four, then a further 10% on the gap BETWEEN words, then a flat +1200ms on that same gap. It is
  // the timing that decides whether a child has time to look at the next word or only to lunge at
  // it, so it has taken every loosening; the tilt and the fall have not moved since the 30%. The
  // point of the crumbling board is to nudge a dawdling child along, not to panic one — and for a
  // kindergarten or first-grade player the original pace turned the moment the board started to
  // give way into the most stressful part of the game. A word now takes ~2.5s from its first tilt
  // to leaving the screen, and a full board ~16s instead of ~12s, which is enough time to read a
  // word and decide rather than just grab.
  /**
   * Trim on the finished clock — everyone gets a little less time than the pace-and-spread rule
   * alone would give them, and whoever needs less time per board gets a little less still.
   *
   * See src/game/level.ts `clockTrimFor` for why this cannot be expressed as a smaller `k`, and
   * specs/adaptive-difficulty.md §7 for why the grading is keyed on the pace rather than on stars.
   *
   *   clockTrimSlow / clockTrimSlowMs   the share, and the board time at or above which it applies
   *   clockTrimFast / clockTrimFastMs   the share for a player at or below that board time
   *
   * Log-interpolated between the two, because board times are multiplicative — halving a pace
   * should move the trim by the same amount wherever you start from.
   */
  clockTrimSlow: 0.95,
  clockTrimSlowMs: 40_000,
  clockTrimFast: 0.93,
  clockTrimFastMs: 8_000,
  cascadeMinMs: 2916,
  cascadeMaxMs: 3774,
  cornerGapMs: 650,
  /**
   * FLOOR on the fall — no longer a fixed duration. See specs/adaptive-difficulty.md §2.
   *
   * The collapse is meant to be raceable: a word tilts, falls, and stays shootable until it leaves
   * the screen. Whether that is a real chance depends entirely on how fast you read, and these
   * timings were absolute — so an adult (~0.35s per word) could save several words inside the 2.47s
   * window while a first-grader (~2.6s per word) could save roughly none. The trigger rescaled with
   * the player and the punishment did not; measured, that was the ONLY place in the mechanic where
   * being slow genuinely costs more.
   *
   * The fall now stretches with the player's own seconds-per-tile, floored here, so slow readers get
   * proportionally more.
   *
   * **1820 was briefly lowered to 800 and put back — see specs/adaptive-difficulty.md §6.1.** The
   * argument for lowering it was that the floor can only bind for a FAST player, so it was a policy
   * about adults and nobody else. That argument was wrong in one specific way, and play found it
   * before the sweep did: the floor was compared against each cohort's MEDIAN per-tile time, but the
   * rescue window is computed from `pace` at the moment a board crumbles — and a board only crumbles
   * when it runs long against the player's recent boards, i.e. when the pace is drawn from their
   * FASTER ones. So the floor binds far more often than a median-vs-floor comparison suggests, and
   * for mid-skill players it was the difference between a word that can be chased and one that
   * cannot. It also set how much the collapse speed VARIES from board to board: without a floor
   * under it, a cascade after three quick boards is visibly faster than one after three slow boards,
   * which reads as the game being inconsistent rather than as the player being quick.
   *
   * What it bought was 0.002 hearts a board on the adult. That is not worth an unraceable collapse.
   */
  fallMs: 1820,
  /**
   * Rescue window as a multiple of the player's own per-tile time (pace ÷ tiles on the board).
   *
   * 1.0 said "you get one word's worth of reading time to save a falling word", which sounds fair
   * and is not: the word that falls is not the word you were looking at, so the real cost is
   * NOTICE + read + decide + aim, against a budget sized for read alone. Played, that is not enough
   * — a mid-skill player watches words they could name go past. 1.4 is the first value that leaves
   * room for the noticing. It only moves players the floor does not already cover (a board slower
   * than ~14s), which is exactly the mid-and-slower range the complaint came from.
   */
  rescuePerWord: 1.4,
  /**
   * Ceiling on the rescue window, so a very slow pace cannot leave a word hanging indefinitely.
   *
   * 9000 → 11000 when `rescuePerWord` went to 1.4: at 9000 the cap started binding on an ordinary
   * observed first-grader (50s boards → 8.75s wanted), and a guard that binds in normal play is not
   * a guard, it is the rule — the same mistake `minSpreadRatio` made at 0.20. It now binds above a
   * ~63s board, which is a child in real trouble and exactly who should not also be racing a clock.
   */
  rescueMaxMs: 11_000,
};

/**
 * Ladybird — the *alternative* slow-play timeout action (specs/crumbling-board.md §4b). When the
 * board would crumble, there's a `chance` it instead sends in a ladybird that crawls word to word
 * and eats each one letter by letter. A fully-eaten *target* costs a life (same as a fallen one);
 * the bug is not shootable — you race it by shooting words before it finishes them.
 *  - chance: probability the timeout spawns the ladybird instead of crumbling the board
 *  - biteMs: time to eat one letter (one bite)
 *  - crawlSpeed: px/second the bug crawls toward its next word
 */
export const LADYBIRD = {
  chance: 0.2,
  biteMs: 400,
  crawlSpeed: 190,
};

/**
 * Survival Escalation mode (the UFO reward minigame). Words fall from the top and speed up
 * gradually — not on a timer, but with each correct smash, so the run gets harder only as the
 * player succeeds. The run is endless until the player taps a word that does NOT match the rule —
 * escaped words just disappear. Tunables:
 *  - spawn/lifetime start+min: start values that ramp toward their floors
 *    (lifetime = fall time; bigger = slower fall)
 *  - rampPerHit: each correct smash multiplies both the spawn interval and the fall time by this
 *    (just under 1 = a tiny speed-up per hit; ~60 smashes to reach the floors)
 *  - targetRatio: chance a spawned word is a target (vs. a decoy of another class)
 *  - workingSetSize: how many of the learned-first words to draw from
 */
export const SURVIVAL = {
  spawnStartMs: 1100,
  spawnMinMs: 360,
  lifetimeStartMs: 7000,
  lifetimeMinMs: 2700,
  rampPerHit: 0.985,
  targetRatio: 0.6,
  workingSetSize: 30,
};

/**
 * Diamond Pairs — the Saturn reward minigame (specs/pairs-mode.md). The calm counterpart to
 * Survival: turn two cards, one a picture and one the WORD for it, so a pair can only be found by
 * reading. No clock, no falling anything; the run ends when the brains do.
 *  - startBrains:    lives; a mismatch costs one only if BOTH cards were already seen (§5.2).
 *                    Five, like the round mode's hearts, and drawn in the same corner (§8.2).
 *  - ladder:         pairs per round; the last entry repeats forever (§4.1)
 *  - grids:          [cols, rows] per ladder entry, landscape (1280×720); cols×rows = 2×pairs
 *  - mismatchMs:     how long a wrong pair stays face-up (a tap cuts it short). 1400, not the
 *                    900 it shipped with: a reader still sounding out the second card had it
 *                    taken away mid-word, and the impatient player has the tap.
 *  - flipMs:         one card's flip
 *  - workingSetSize: how many of the learned-first picture words to SAMPLE from (§6.4 — sampled,
 *                    not taken off the top, or every run would deal the same round 1)
 *  - topicsPerRound: how many icon topics one round is sampled from
 *  - minFontPx:      word-face font floor; below it the word is not deck-eligible (§6.3)
 *  - maxWordChars:   deck eligibility by length, checked as data by picturewords-cardfit.test.ts
 */
/**
 * Ice Maze — the Neptune reward minigame (specs/word-maze-mode.md).
 *  - startFlakes:   lives; the CLOCK is the only thing that melts one (§5.3, §5.5)
 *  - grids:         [cols, rows] per rung; the last repeats forever (§4.1). The grid grows sideways
 *                   rather than squarely: the canvas is 16:9 and the ask sits in the banner, so
 *                   width is the free dimension and height is the one that caps the tile size.
 *
 *                   COLUMNS are the floor nobody can lower: a word needs `cols - 1` cells, so a
 *                   7-wide first board already caps its words at 6 clusters, where 13-15 of the 15
 *                   topics still have enough words in every language. At 6 wide, Italian and French
 *                   fall to 10 usable topics and the mode starts repeating itself. Rows are the
 *                   free dimension, which is why the gentle first rung is short rather than narrow.
 *  - targets:       words to find per rung
 *  - timeMs:        per-board clock per rung
 *  - diagonalsFrom: first round that also hides ↘ and ↗. The four REVERSED directions are never
 *                   used at any round — a child still assembling the left-to-right sweep must not
 *                   be rewarded for reading backwards (§5.2).
 *  - minCells/minCellsDense/maxCells: word length in grid cells; the floor drops for ja/ko/zh,
 *                   whose scripts pack a syllable into one cell
 *  - workingSetSize: how many of the learned-first picture words to sample from
 *  - dealAttempts:  generator retries before falling back to the shortest words (§7)
 */
/**
 * Word Wagon — the galaxy reward minigame (specs/wagon-mode.md).
 *
 *  - startWagons:    lives — the spare wagons standing in the yard, drawn as 🚃. TWO things wreck
 *                    one, one per kind of misread: a hazard CAUGHT, and a lane OVERFLOWING because
 *                    its targets kept being missed (§5.4). The wagon is the thing the child drives,
 *                    so it is also the thing they can lose; the LOAD it is carrying survives both
 *                    (§5.2), and loads shipped is the run's score
 *  - lanes/load/fallMs/spawnMs/targetRatio/stackCap: the ladder, one row per wagon; last repeats
 *  - laneMs:         travel per lane. CONSTANT across the ladder on purpose — the fall shortens
 *                    instead, so the ladder tightens READING time and never finger speed (§4.1).
 *                    The real budget is fallMs - laneMs * (lanes - 1): 3640 ms at the first rung,
 *                    1760 ms at the last (§4.2)
 *  - signMs:         how long a new destination sign is held before the freighter starts again.
 *                    The class may never change under a word already falling (§5.6)
 *  - sacrificeFrom:  first wagon whose schedule may make two targets mutually exclusive. Hazards
 *                    stay avoidable at EVERY rung — the board may make you drop a word, never eat
 *                    one (§7.2)
 *  - minSameLaneGapMs: two words in one lane closer than this arrive as one object (§7.3)
 *  - minTargets/minHazards: floor for a class to enter the sign rotation (§6.2)
 *  - workingSetSize: cargo pool per run; the same 30 Survival draws from (§6.3)
 *  - dealAttempts:   re-rolls before the spawner widens the gap rather than ship an unfair drop
 */
export const WAGON = {
  startWagons: 5,
  lanes: [5, 5, 6, 6, 7],
  load: [4, 5, 6, 7, 8],
  fallMs: [4200, 3800, 3400, 3000, 2600],
  spawnMs: [1500, 1350, 1200, 1050, 900],
  targetRatio: [0.55, 0.5, 0.5, 0.45, 0.45],
  // Three crates may sit in a lane; the FOURTH buries it. It was [4, 4, 3, 3, 3], so the opening
  // rungs ended on the fifth crate and the later ones on the fourth — the rule a child learns in
  // round 1 quietly changed under them at round 3. One number now, and the count they can see on
  // the pile is the count that matters.
  stackCap: [3, 3, 3, 3, 3],
  laneMs: 140,
  signMs: 1200,
  sacrificeFrom: 5,
  minSameLaneGapMs: 900,
  minTargets: 12,
  minHazards: 12,
  minFontPx: 20,
  workingSetSize: 30,
  dealAttempts: 12,
};

export const LABYRINTH = {
  /**
   * Lives. TWO things drain one: a wrong turn (§5.3) and the clock (§5.6). Five, flat, matching
   * ❤️ lives, 🧠 brains and ❄️ flakes so the treat modes read as a set — which is also what makes
   * four rounds a strong run, hence `LABYRINTH_MIN = 4` rather than 6.
   */
  startBatteries: 5,

  /**
   * `[cols, rows]` per rung; the last repeats forever.
   *
   * **`cols` is a CAP, not a width.** The path must run from the left edge to the right edge, so a
   * maze can never be wider than the sentence is long — a 3-word sentence on a 4-column grid has
   * no way to reach the far side. The dealer therefore lays out `min(cols, path.length)` columns
   * (see `maze.ts`), which is what lets ja/ko/th play the same ladder: their sentences top out at 4
   * path cells and would otherwise be undealable from round 2 on.
   *
   * The grid grows UPWARD, not sideways — a cell holds a whole word, so width is what it cannot
   * give up. The opposite of `MAZE.grids`, for the same underlying reason.
   */
  grids: [[4, 3], [5, 4], [6, 4], [6, 5], [7, 5]] as [number, number][],

  /** `[min, max]` cells on the true path per rung. A sentence outside the band is not dealt. */
  pathCells: [[3, 5], [3, 7], [6, 9], [6, 9], [6, 10]] as [number, number][],

  /**
   * Wrong turns a board may offer per rung — the "Forks" column of §4.1's ladder.
   *
   * This is a **cap the generator enforces**, not a description of what it happens to produce. Left
   * uncapped, braiding a 6×4 grid yields around nine path→branch corridors at round 3, and since
   * every one of them costs a battery (§5.3) that is a board a child can lose twice over before
   * they have read anything. The cap is what makes the battery economy of §5.5 arithmetic rather
   * than hope.
   */
  forks: [2, 3, 4, 5, 6],

  /** Per-board clock per rung. */
  timeMs: [60_000, 65_000, 80_000, 90_000, 100_000],

  /**
   * First round whose path carries two sentences (§6.4 grade 1). Rounds 1–2 are the tutorial rung:
   * a sentence small enough to hold in your head while you learn what a step does.
   */
  bends: [1, 2, 3, 4, 5],
  chainFrom: 3,
  gateAutoMs: 1000,
  clearHoldMs: 1200,
  clearCapMs: 7000,

  /**
   * First round that PREFERS a grammatically joined pair where the language has one (§6.4 grade 2).
   * 6–10 per Latin locale and **zero** in ja/ko/th, so this must always fall back to a plain chain
   * and can never be required.
   */
  joinFrom: 2,

  /**
   * Distinct routes from entrance to gate the maze must have. THREE, not one: a perfect maze — the
   * kind every textbook generator produces — is solvable with the words switched off, which is the
   * paper-word-search failure in maze form (§7.1).
   */
  minExitRoutes: 3,

  /**
   * Cells a wrong branch must run before it ends. A one-cell stub is visibly a stub, and a child
   * who has just paid a battery for it must have something to read.
   */
  minDeadEndLen: 2,

  /** Sets with fewer authored decoys are skipped by the dealer (§6.3). */
  minDistinctDecoys: 3,

  /** A longer word falls under `minFontPx` at the round-5 cell width (§6.5). */
  maxWordChars: 14,

  minFontPx: 18,

  /** How many learned-first sentence sets to sample from. */
  workingSetSize: 40,

  /** Generator retries before relaxing the route/dead-end targets (§7.2). */
  dealAttempts: 12,
};

export const MAZE = {
  startFlakes: 5,
  grids: [[7, 5], [8, 6], [10, 6], [12, 7], [13, 8]] as [number, number][],
  targets: [3, 4, 4, 5, 5],
  timeMs: [50_000, 60_000, 65_000, 75_000, 85_000],
  diagonalsFrom: 6,
  minCells: 3,
  minCellsDense: 2,
  maxCells: 8,
  workingSetSize: 40,
  dealAttempts: 12,
};

/**
 * Recipe Kitchen — the eclipse reward minigame (specs/recipe-mode.md).
 *
 * The ladder is per ROUND, and every row says the same three things: how many tiles are on the
 * shelf, how long the flame burns, and how many ingredients the card may ask for. The last row
 * repeats forever.
 */
export const RECIPE = {
  /**
   * Lives. TWO things burn one: a wrong ingredient (§5.2) and the flame running out (§5.3). Five,
   * flat, matching ❤️ lives, 🧠 brains, ❄️ flakes, 🔋 batteries and 🚃 spare wagons, so the treat
   * modes read as a set.
   */
  startCorn: 5,

  /**
   * `[shelfTiles, ms, minIngredients, maxIngredients, foldAfterMs]` per rung; the last repeats.
   *
   * **Both numbers tighten every round**, which is the mode's whole progression: the clock falls
   * from 40 s to 24 s, and the window in which the card stays open falls from "never closes" to
   * 3.5 s. The tile count grows alongside, so what a rung asks for is more shelf to reject in less
   * time with a shorter look at the list.
   *
   * The clock used to RISE at round 4 — the 4-ingredient rung was given more time because it needs
   * more taps — which meant the game got easier exactly where it should have started biting.
   * Ingredients now climb one rung later instead, so falling time stays fair.
   *
   * `foldAfterMs` of 0 means the card never folds (rounds 1–2, the tutorial rungs). Folding is a
   * reading curve, not a memory test: tapping the folded card opens it again for as long as a
   * finger stays down, as often as the child likes (§4.3). What the fold buys is that they have to
   * *decide* to look — the behaviour worth building — and it costs only clock.
   *
   * The band widens as it climbs, and **row 8 opens the whole book** (3–5). Two reasons: a long run
   * should stop feeling like four dishes on a loop, and mastering every recipe in one run (§5.5)
   * has to be reachable at all — with the top rung locked to 4–5 ingredients the eight
   * three-ingredient dishes could never come back, so "all of them" was unwinnable by construction.
   */
  rounds: [
    [6, 40_000, 3, 3, 0],
    [8, 36_000, 3, 3, 0],
    [9, 33_000, 3, 3, 8_000],
    [10, 31_000, 3, 4, 7_000],
    [12, 29_000, 4, 4, 6_000],
    [12, 27_000, 4, 4, 5_000],
    [12, 25_000, 4, 5, 4_000],
    [12, 24_000, 3, 5, 3_500],
  ] as [number, number, number, number, number][],

  /** How long a wrongly tapped tile shows its own name before it goes back (§5.2). */
  wrongNameMs: 1200,

  /**
   * The tail of the flame in which a wrong tap is charged to the clock instead of to the tile. A
   * tap that lands as the flame goes out must not cost two corn.
   */
  graceMs: 400,

  /** Fraction of the flame that must be left for a clean dish to hand a corn back (§5.4). */
  cornBackAtFlame: 0.5,

  /** How long the finished dish is held before the next card, unless a tap cuts it short. */
  dishHoldMs: 2200,

  /** How long „Angebrannt!" is held before the run's result card takes over (§5.3). */
  burnToEndMs: 1200,


  /**
   * How long the result card waits before returning to the menu on its own.
   *
   * Two values because the two ways a run ends deserve different amounts of reading time. A burnt
   * round is an interruption — the child was mid-recipe and the clock took it away — so it goes
   * back quickly. Running out of corn is the run's ENDING, with a dish count and possibly a new
   * record on it, so it holds long enough to be read. Either way a tap leaves at once, and neither
   * can strand a child on a card that answers nothing.
   */
  autoLeaveBurnMs: 4_000,
  autoLeaveOverMs: 12_000,

  /** Minimum decoys a shelf must be able to draw, or the dealer widens its candidate set. */
  minDecoys: 2,
};

export const PAIRS = {
  startBrains: 5,
  ladder: [6, 8, 10, 12],
  grids: [[4, 3], [4, 4], [5, 4], [6, 4]] as [number, number][],
  mismatchMs: 1400,
  flipMs: 180,
  workingSetSize: 40,
  topicsPerRound: 3,
  minFontPx: 18,
  maxWordChars: 16,
};

/**
 * Cat & Mouse — the Venus treat's letter hunt (specs/cat-mouse-mode.md §12).
 *
 * The one block in this file with no `timeMs`, and that absence IS the mode: every other minigame
 * is paced by a clock, and this one is aimed at a four-year-old who reads a letter in three seconds
 * one day and ten the next. Nothing here may grow into a timer.
 */
/**
 * Word boards at the TAIL of a Cat run (§7) — the picture doors, and the length of the run.
 *
 * The run used to be five boards, three of letters and two of words, and it was over before the
 * part that makes letters mean anything had really started. The three letter boards are the warm-up
 * and they stayed three; everything after them is a picture door with a different word on it.
 *
 * The ceiling is the picture pool, not this number: `wordCandidates` finds 49–96 words a child can
 * be asked to spell in each of the ten languages once the alphabet is met, and `pickDoorWord` takes
 * the shortest unused one each time, so the doors get longer across the run on their own.
 */
const CAT_WORD_BOARDS = 12;

const catTail = <T>(make: () => T): T[] => Array.from({ length: CAT_WORD_BOARDS }, make);

export const CAT = {
  /**
   * Lives, PER BOARD, restored at every door (§5.4). Exactly one thing drains one: stepping on a
   * decoy. Three rather than the five that ❤️ lives, 🧠 brains, ❄️ flakes and 🔋 batteries all use
   * — three is a number a child holds as a picture, and a board with 14 decoys and five lives is a
   * board that can be brute-forced.
   */
  startFish: 3,

  /** Out of fish: how long "the mouse got away" holds before the run returns to the menu (§5.4). */
  gotAwayToMenuMs: 2_500,

  /**
   * Boards per run. Fixed, not lives-limited like the Ice Maze and Labyrinth ladders. Three letter
   * boards and then `CAT_WORD_BOARDS` picture doors; the ladder arrays below all have exactly this
   * length.
   */
  boards: 3 + CAT_WORD_BOARDS,

  /**
   * `[cols, rows]` per board. Grows both ways, unlike LABYRINTH.grids — a cell holds one glyph
   * rather than a whole word, so width is not the thing that cannot be given up.
   *
   * 5x4 is where it stops: the word boards all share it, because the longest word the door may
   * wear is capped at `wordCells` and a bigger grid past that is empty floor, not more game.
   */
  grids: [[3, 3], [4, 3], [4, 4], ...catTail<[number, number]>(() => [5, 4])] as [number, number][],

  /**
   * Active classes — and therefore doors (§4.4) — per board. **A 0 means a word board** (§7): its
   * door wears a picture instead of letters and its targets are that word's letters, in order.
   *
   * Three boards of "find both drawings of this letter", and then nothing but words — the rung the
   * mode exists to reach. See CAT_WORD_BOARDS.
   */
  classes: [1, 1, 2, ...catTail(() => 0)],

  /**
   * Targets per board. A word board takes its count from the word's length instead (§7.1), and a
   * word board that cannot be dealt falls back to an ordinary two-target board — which is what the
   * 0s here mean for the first runs of a brand-new profile, where almost no letter has been met.
   */
  targets: [2, 3, 4, ...catTail(() => 0)],

  /**
   * Decoy letters per rung — and the rest of the grid is **empty floor the cat walks for free**.
   *
   * This is not a cosmetic ratio, it is what makes the board playable at all, and it was found by
   * playing it rather than by writing it: with a letter on every cell (which is what §4.3's first
   * anatomy drew) every step that is not a target costs a fish, so a cat with three fish cannot
   * cross a 5x4 grid to reach anything. The board was a minefield wearing a maze's clothes.
   *
   * `deal.ts` also guarantees a route to every target and to the doors that touches **no decoy at
   * all**, so a careful child can always finish a board without losing a fish. The decoys sit off
   * that route, where stepping on one is a choice rather than a toll.
   */
  decoys: [3, 4, 5, ...catTail(() => 6)],

  /**
   * First board (1-based) whose decoys come ONLY from the targets' `decoyPreferClasses` plus
   * classes the child already knows — the point where the run stops being "find the K" and becomes
   * "tell K from the things that look like K" (§8.3).
   */
  lookalikeOnlyFrom: 4,

  /** Unknown classes open at once — the gated window of §8.2, opened in `rank` order. */
  activeClasses: 4,

  /** Boards cleared without losing a fish before a class counts as KNOWN (§8.2). */
  knownAt: 3,

  /** Max cells a picture door's word may occupy; lower in dense scripts (ja/ko) — §7.2. */
  wordCells: 5,
  wordCellsDense: 4,
};

/**
 * Space Worm — the red-Sun treat (specs/space-worm-mode.md §11).
 *
 * Pedagogy and feel in one place, not scattered through the scene. Two numbers here carry an
 * argument rather than a preference and should not be nudged without reading their sections:
 * `startBooks` (§6.1 — five, and the only thing a run can be lost by) and `decoys` (§4.4 — a
 * shallower ladder than the first draft's, because a decoy costs the word as well as the book).
 */
export const WORM = {
  /**
   * Lives per RUN (§6.1). Not restored between words, and **the only thing a run can be lost by**.
   *
   * Three was set when a knot ended the run outright, so the books only had to cover misreadings. A
   * knot now costs a book and the word like any other mistake (§5.5), so the same books cover both
   * kinds — and a run that ended because the child drove badly three times was over before they had
   * read anything.
   */
  startBooks: 5,

  /** Words to finish for a complete run. Only a FINISHED word advances the rung (§4.3). */
  words: 5,

  /**
   * The play area — the board below the HUD band. The worm leaves one edge and arrives at the
   * opposite one (§5.2); nothing here is a grid, and nothing here may become one (§5.1).
   */
  play: { x: 0, y: 190, w: GAME_WIDTH, h: GAME_HEIGHT - 190 },

  /** Steps wanted per rung; the dealer falls to the nearest length it can actually offer (§7.2). */
  steps: [3, 4, 4, 5, 5],

  /** Decoy letters per rung (§7.3). Rung 1 has none on purpose. */
  decoys: [0, 2, 3, 4, 5],

  /** Pixels per second, constant within a rung (§5.7 — it never speeds up as you eat). */
  speed: [210, 235, 260, 285, 310],

  /**
   * How far past the edge the worm may slip before it turns itself round and comes back (§5.2).
   *
   * The board is not a torus: leaving the right edge no longer means arriving on the left, because
   * that is not a move a child can plan and it puts the worm where they are not looking. It may go
   * out of sight — a marker says where — but never away.
   */
  outsideMargin: 90,

  /**
   * How fast the worm may turn while it is off the board, as a multiple of its normal rate.
   *
   * **One.** The first version boosted it to 2.6, on the theory that out there the worm is not
   * playing, it is returning, and should turn sharply. What a 2.6x rate actually is, though, is a
   * turning circle of 16px — and a worm carving a 16px circle wraps around its own neck inside half
   * a second. It came home as a knot (§17.18).
   *
   * At the normal rate a U-turn from 90px out takes about 0.6s, which was never the problem the
   * boost was solving. Kept as a named knob rather than deleted, because the next person to think
   * "it should snap back faster" deserves to find the reason it does not.
   */
  outsideTurnBoost: 1,

  /**
   * The tightest circle the worm can carve, in pixels — and therefore how fast it may turn, which
   * is `speed / turnRadius` and so differs per rung.
   *
   * A RADIUS rather than a rate, because the radius is what the knot depends on: at full tilt the
   * worm draws a circle of circumference `2 * PI * 42` = 264px, and a body longer than that can
   * close on itself (§5.5). At `beadSpacing` 36 that is **eight segments — the fourth letter of the
   * word** — and because the radius is fixed, it is the fourth letter at every rung rather than
   * "whenever the maths happens to line up". A rate instead of a radius made the fast rungs
   * unknottable and the slow ones twitchy.
   */
  turnRadius: 42,

  /**
   * The body: overlapping circles, not a row of beads. `beadSpacing` is deliberately well under
   * `2 * beadRadius`, so each segment overlaps its neighbours and the worm reads as one animal
   * while the lettered segments stay far enough apart to be read.
   */
  beadRadius: 25,
  beadSpacing: 36,
  headRadius: 27,

  /**
   * Segments the worm starts each word with. Four is short enough that it provably cannot reach
   * itself at any speed or heading (`step.test.ts` re-proves that against this number, so raising
   * it is a decision the tests get a vote on) and long enough to read as an animal rather than a
   * blob with eyes.
   */
  startBeads: 4,

  /**
   * Segments at the front that are NOT solid (§5.5). Without a neck, the tightest legal turn is
   * fatal — the head is always a hair away from what is just behind it.
   */
  neckBeads: 3,
  /**
   * How close the head's centre must come to a solid segment to tie the knot.
   *
   * 44 against a head of 27 and a segment of 25: the circles have to visibly overlap, and the
   * number has ~8px of slack in it on purpose. The worm's path is walked in frame-sized chords, so
   * the distance it passes a segment at wobbles by a few pixels with the frame rate — at a hair
   * under the true grazing distance, one rung's speed would have been unknottable for no reason a
   * player could ever see.
   */
  hitRadius: 44,
  /** How far ahead the head looks for the bead it is about to hit, so it can pulse (§5.5 rule 3). */
  dangerPx: 64,

  /**
   * Arriving at the point the finger asked for (§5.1.3): inside `arriveRadius`, or within
   * `arriveNearPx` and already moving away again — the shape of an orbit's top.
   *
   * `arriveNearPx` has to be comfortably wider than the turning circle (42px), because a worm whose
   * circle is wider than the arrival ring would otherwise orbit forever without ever "arriving".
   */
  arriveRadius: 34,
  arriveNearPx: 130,

  /** Letter tiles: how big they are drawn, and how close the head must come to eat one. */
  tileRadius: 30,
  biteRadius: 44,

  /**
   * Inset from the edge of the play area for a tile's CENTRE, so no letter is ever drawn half off
   * the board. The worm still arrives from every side — it wraps, the letters do not.
   */
  edgeInset: 40,

  /** Placement (§7.4), all in pixels: tiles apart, clear of the head, and the runway ahead of it. */
  minTileGap: 116,
  headClear: 190,
  /**
   * Kept UNDER half the board's height on purpose: on a torus, a corridor longer than half the
   * board wraps round and the worm ends up inside its own runway, which is both a nonsense and a
   * dealer that refuses to place anything. 240px is about a second of reading time at every rung.
   */
  runwayPx: 240,
  runwayHalfWidth: 84,
  /** Samples before the placer drops a decoy and retries. */
  placeTries: 600,

  /** Rungs on which the word is written out under the picture (§4.3). */
  showWordRungs: 2,
  /** Rung on which the written word fades away after the first bite. */
  fadeWordRung: 3,

  /**
   * How long the worm stands still when a new word appears (§5.1.4).
   *
   * A board arrives with a picture to read, a word to find the first letter of, and letters
   * twinkling into place — and the old version had the worm already crossing it at 210px/s while
   * all that was still happening. Two seconds of nothing is what makes the first letter a decision
   * rather than a reflex, and it is the difference between a run that starts and a run that is
   * already going wrong.
   *
   * It was 1200 first and that was measurably not enough: the letters alone take ~280ms plus 40ms
   * a tile to arrive, so a board of ten tiles is still assembling for the first half of it.
   */
  startHoldMs: 2200,

  /** How long the „Verknotet!" banner holds before the board is re-dealt (§5.5). */
  knotHoldMs: 1500,

  /** The straightening (§5.6), the wrong-bite flash (§5.4). */
  straightenMs: 2000,
  wrongFlashMs: 200,

  /** Letters arrive and leave as stars (§5.4.1): per-tile fade, and the stagger across the board. */
  materializeMs: 280,
  materializeStaggerMs: 40,
  /** The whole beat a lost word costs: dissolve, spoken correction, new word (§5.4). */
  wrongWordMs: 1100,
};
